import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtMoney, fmtDate, fmtDateTime, statutLabel, todayISO, DEVIS_STATUTS, STATUTS_PARKES } from "../lib/format";
import { genererDevisPDF, genererProformaPDF } from "../lib/pdf";
import { Modal, Field, Badge, Spinner } from "../components/ui";
import { SearchSelect, orgOptions } from "../components/SearchSelect";
import { RefSelect } from "../components/RefSelect";

const TVA_RATES = [0, 7, 13, 19];

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, isNaN(v) ? min : v));

function computeTotals(lignes: any[], remiseGlob: number, tvaPct: number, retenuePct: number) {
  let brut = 0, net = 0;
  for (const l of lignes) {
    if ((l.type || "ligne") !== "ligne") continue;
    const q = +l.qte || 0, pu = +l.prix_unitaire || 0, rem = +l.remise_pct || 0;
    brut += q * pu;
    net += q * pu * (1 - rem / 100);
  }
  const rg = (net * (+remiseGlob || 0)) / 100;
  const ht = net - rg;
  const tva = (ht * (+tvaPct || 0)) / 100;
  const ttc = ht + tva;
  const retenue = (ttc * (+retenuePct || 0)) / 100;
  return { brut, net, rg, ht, tva, ttc, retenue, napayer: ttc - retenue };
}

function OrgQuickForm({ type, onCreated, onClose }: any) {
  const [f, setF] = useState<any>({ type, nom: "", pays: "" });
  const [err, setErr] = useState<string | null>(null);
  return (
    <Modal title={`Nouvelle organisation (${type})`} onClose={onClose} testId="org-quick-modal">
      <div className="space-y-3">
        <Field label="Nom">
          <input data-testid="org-quick-nom" className="nx-input" value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} />
        </Field>
        <Field label="Pays">
          <input data-testid="org-quick-pays" className="nx-input" value={f.pays} onChange={(e) => setF({ ...f, pays: e.target.value })} />
        </Field>
        {err && <p className="text-sm text-danger">{err}</p>}
        <div className="flex justify-end gap-2">
          <button className="nx-btn-ghost" onClick={onClose}>Annuler</button>
          <button
            data-testid="org-quick-save"
            className="nx-btn-primary"
            disabled={!f.nom}
            onClick={async () => {
              try {
                const { data } = await api.post("/organisations", f);
                onCreated(data);
              } catch (e) {
                setErr(apiError(e));
              }
            }}
          >
            Créer
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function DevisBuilder() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const mgr = user?.role === "manager";
  const [d, setD] = useState<any>(null);
  const [orgs, setOrgs] = useState<any[]>([]);
  const [produits, setProduits] = useState<any[]>([]);
  const [entites, setEntites] = useState<any[]>([]);
  const [refs, setRefs] = useState<any>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [quickOrg, setQuickOrg] = useState<{ type: string; target: string } | null>(null);
  const [pdfModal, setPdfModal] = useState<string | null>(null);
  const [pdfForm, setPdfForm] = useState<any>({});
  const [sendModal, setSendModal] = useState<any>(null);
  const [sendForm, setSendForm] = useState<any>({});
  const [sendErr, setSendErr] = useState<string | null>(null);
  const [mailto, setMailto] = useState<any>(null);
  const [savedStatut, setSavedStatut] = useState<string | null>(null);
  const dragIdx = useRef<number | null>(null);

  const setField = useCallback((patch: any) => {
    setD((prev: any) => ({ ...prev, ...patch }));
    setDirty(true);
  }, []);

  useEffect(() => {
    api.get("/organisations").then((r) => setOrgs(r.data));
    api.get("/produits").then((r) => setProduits(r.data));
    api.get("/entites").then((r) => setEntites(r.data));
    api.get("/referentiels").then((r) => setRefs(r.data));
    if (id) {
      api.get(`/devis/${id}`).then((r) => { setD(r.data); setSavedStatut(r.data.statut); }).catch((e) => { alert(apiError(e)); nav("/devis"); });
    } else {
      setSavedStatut(null);
      setD({
        statut: "brouillon", devise: "EUR", cf_sur_document: false, conditions: [],
        remise_globale_pct: 0, tva_pct: 0, retenue_pct: 0, note_libre_on: false,
        remise_label: "Remise revendeur",
        date_devis: new Date().toISOString().slice(0, 10),
        date_cloture_estimee: null,
        lignes: [{ type: "ligne", designation: "", qte: 1, prix_unitaire: 0, remise_pct: 0, tva_pct: 0 }],
        relances: [], documents: [],
      });
    }
  }, [id, nav]);

  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const totals = useMemo(
    () => (d ? computeTotals(d.lignes || [], d.remise_globale_pct, d.tva_pct, d.retenue_pct) : null),
    [d]
  );

  if (!d || !refs || !totals) return <Spinner />;

  const produit = produits.find((p) => p.id === d.produit_id);
  const sousProduits = produit?.sous_produits || [];
  const clients = orgs.filter((o) => o.type === "client" || o.type === "revendeur");
  const revendeurs = orgs.filter((o) => o.type === "revendeur");
  const listes = refs.listes || {};
  const motifAutre = (listes.motif_perte || []).find((m: any) => m.code === "autre")?.libelle || "Autre";
  const parque = STATUTS_PARKES.includes(savedStatut || "");
  const perduFige = savedStatut === "perdu" && d.statut === "perdu";

  const save = async (): Promise<any> => {
    setSaving(true);
    setErr(null);
    try {
      let saved: any;
      if (d.id) {
        const { data } = await api.put(`/devis/${d.id}`, d);
        saved = { ...d, ...data };
      } else {
        const { data } = await api.post("/devis", d);
        saved = { ...d, ...data };
        nav(`/devis/${data.id}`, { replace: true });
      }
      setD((prev: any) => ({ ...prev, ...saved }));
      setSavedStatut(saved.statut);
      setDirty(false);
      if (STATUTS_PARKES.includes(saved.statut) || STATUTS_PARKES.includes(savedStatut || "")) {
        api.get(`/devis/${saved.id}`).then((r) => setD((prev: any) => ({ ...prev, historique_perte: r.data.historique_perte })));
      }
      return saved;
    } catch (e) {
      setErr(apiError(e));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const reactiver = async () => {
    setBusy("reactiver");
    setErr(null);
    try {
      await api.post(`/devis/${d.id}/reactiver`);
      const { data } = await api.get(`/devis/${d.id}`);
      setD(data);
      setSavedStatut(data.statut);
      setDirty(false);
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const reloadDocs = () => {
    if (d.id) api.get(`/devis/${d.id}`).then((r) => setD((prev: any) => ({ ...prev, documents: r.data.documents, relances: r.data.relances })));
  };

  const ensureSaved = async (): Promise<any> => {
    if (dirty || !d.id) return await save();
    return d;
  };

  const genDevis = async () => {
    if (busy) return;
    setBusy("devis");
    try {
      const saved = await ensureSaved();
      if (!saved) return;
      const clientOrg = orgs.find((o) => o.id === saved.client_id);
      const cfOrg = orgs.find((o) => o.id === saved.client_final_id);
      await genererDevisPDF(saved, clientOrg, cfOrg);
      reloadDocs();
    } catch (e) {
      setErr(`Échec de la génération du PDF : ${apiError(e)}`);
    } finally {
      setBusy(null);
    }
  };

  const genProforma = async (facture: boolean) => {
    if (busy) return;
    setBusy("proforma");
    try {
      const saved = await ensureSaved();
      if (!saved) return;
      const entite = entites.find((e) => e.id === pdfForm.entite_id);
      if (!entite) return;
      const clientOrg = orgs.find((o) => o.id === saved.client_id);
      await genererProformaPDF(saved, clientOrg, entite, { facture, paid: !!pdfForm.paid });
      setPdfModal(null);
      reloadDocs();
    } catch (e) {
      setPdfModal(null);
      setErr(`Échec de la génération du PDF : ${apiError(e)}`);
    } finally {
      setBusy(null);
    }
  };

  const creerBon = async () => {
    const saved = await ensureSaved();
    if (!saved) return;
    try {
      const { data } = await api.post("/bons-commande", {
        devis_id: saved.id,
        client_final_id: saved.client_final_id,
        revendeur_id: saved.revendeur_id,
        entite_id: saved.entite_id,
        objet: saved.objet,
        devise: saved.devise,
      });
      nav(`/bons-commande?bon=${data.id}`);
    } catch (e) {
      alert(apiError(e));
    }
  };

  const openSend = async () => {
    const saved = await ensureSaved();
    if (!saved) return;
    const docs = saved.documents || d.documents || [];
    setSendForm({
      document_id: docs[0]?.id || "",
      destinataires: "",
      sujet: `Naxium — Devis N° ${saved.numero}${saved.objet ? " — " + saved.objet : ""}`,
      corps: `Bonjour,\n\nVeuillez trouver ci-joint notre devis N° ${saved.numero}.\n\nRestant à votre disposition,\nL'équipe Naxium`,
    });
    setMailto(null);
    setSendErr(null);
    setSendModal(true);
  };

  const doSend = async () => {
    if (busy) return;
    setBusy("send");
    setSendErr(null);
    try {
      const dest = sendForm.destinataires.split(/[,;]/).map((s: string) => s.trim()).filter(Boolean);
      const { data } = await api.post(`/documents/${sendForm.document_id}/send`, {
        destinataires: dest, sujet: sendForm.sujet, corps: sendForm.corps,
      });
      if (data.mailto) setMailto(data.mailto);
      else {
        setSendModal(null);
        alert("Email envoyé avec succès");
      }
      reloadDocs();
    } catch (e) {
      setSendErr(`Échec de l'envoi : ${apiError(e)}`);
    } finally {
      setBusy(null);
    }
  };

  const updateLigne = (i: number, patch: any) => {
    const lignes = [...d.lignes];
    lignes[i] = { ...lignes[i], ...patch };
    setField({ lignes });
  };

  const addLigne = (type: string) => {
    setField({ lignes: [...d.lignes, { type, designation: "", qte: 1, prix_unitaire: 0, remise_pct: 0, tva_pct: 0 }] });
  };

  const removeLigne = (i: number) => {
    setField({ lignes: d.lignes.filter((_: any, x: number) => x !== i) });
  };

  const onDrop = (i: number) => {
    if (dragIdx.current === null || dragIdx.current === i) return;
    const lignes = [...d.lignes];
    const [moved] = lignes.splice(dragIdx.current, 1);
    lignes.splice(i, 0, moved);
    dragIdx.current = null;
    setField({ lignes });
  };

  return (
    <div data-testid="devis-builder-page" className="mx-auto max-w-5xl space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <button className="nx-btn-ghost !px-2.5" onClick={() => { if (!dirty || window.confirm("Modifications non enregistrées. Quitter quand même ?")) nav("/devis"); }} data-testid="devis-back-btn">
          ← Devis
        </button>
        <h1 className="text-xl font-extrabold text-green">
          {d.id ? `Devis N° ${d.numero}` : "Nouveau devis"}
        </h1>
        {dirty && <Badge className="bg-amber-50 text-amber" testId="devis-dirty-badge">Non enregistré</Badge>}
        <div className="ml-auto flex flex-wrap gap-2">
          <button data-testid="devis-save-btn" className="nx-btn-primary" disabled={saving} onClick={save}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
          {d.id && parque && (
            <button data-testid="devis-reactiver-btn" className="nx-btn-lime" disabled={!!busy || saving} onClick={reactiver} title={`Retour au statut « ${statutLabel(d.statut_precedent || "envoye")} »`}>
              {busy === "reactiver" ? "Réactivation…" : `Réactiver → ${statutLabel(d.statut_precedent || "envoye")}`}
            </button>
          )}
          {d.id && (
            <>
              <button data-testid="devis-pdf-btn" className="nx-btn-lime" disabled={!!busy || saving} onClick={genDevis}>{busy === "devis" ? "Génération…" : "PDF Devis"}</button>
              <button data-testid="devis-proforma-btn" className="nx-btn-ghost" disabled={!!busy || saving} onClick={() => { setPdfForm({ entite_id: d.entite_id || "", paid: false, facture: false }); setPdfModal("proforma"); }}>Proforma</button>
              {mgr && <button data-testid="devis-facture-btn" className="nx-btn-ghost" disabled={!!busy || saving} onClick={() => { setPdfForm({ entite_id: d.entite_id || "", paid: false, facture: true }); setPdfModal("facture"); }}>Facture</button>}
              {mgr && <button data-testid="devis-bc-btn" className="nx-btn-ghost" onClick={creerBon}>Créer le BC</button>}
              <button data-testid="devis-send-btn" className="nx-btn-ghost" onClick={openSend}>Envoyer</button>
              <button
                data-testid="devis-duplicate-btn"
                className="nx-btn-ghost"
                disabled={!!busy}
                onClick={async () => {
                  if (busy) return;
                  setBusy("dup");
                  try {
                    const { data } = await api.post(`/devis/${d.id}/duplicate`);
                    nav(`/devis/${data.id}`);
                    window.location.reload();
                  } catch (e) {
                    alert(apiError(e));
                    setBusy(null);
                  }
                }}
              >
                Dupliquer
              </button>
            </>
          )}
        </div>
      </div>
      {err && <p data-testid="devis-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-danger">{err}</p>}

      <div className="nx-card space-y-4 p-5">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Field label="Objet" className="col-span-2">
            <input data-testid="devis-objet-input" className="nx-input" value={d.objet || ""} onChange={(e) => setField({ objet: e.target.value })} />
          </Field>
          <Field label="Date">
            <input data-testid="devis-date-input" type="date" className="nx-input" value={(d.date_devis || "").slice(0, 10)} onChange={(e) => setField({ date_devis: e.target.value })} />
          </Field>
          <Field label="Statut">
            <select data-testid="devis-statut-select" className="nx-input" value={d.statut} onChange={(e) => setField({ statut: e.target.value, probabilite: DEVIS_STATUTS.find((s) => s.key === e.target.value)?.prob })}>
              {DEVIS_STATUTS.map((s) => (
                <option key={s.key} value={s.key} disabled={parque && !STATUTS_PARKES.includes(s.key)}>
                  {s.label} ({s.prob} %){parque && !STATUTS_PARKES.includes(s.key) ? " — via Réactiver" : ""}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Client">
            <div className="flex gap-1">
              <div className="flex-1"><SearchSelect testId="devis-client-select" options={orgOptions(clients)} value={d.client_id || null} onChange={(v) => setField({ client_id: v })} /></div>
              <button data-testid="devis-client-new-btn" className="nx-btn-ghost !px-2" title="Créer un client" onClick={() => setQuickOrg({ type: "client", target: "client_id" })}>+</button>
            </div>
          </Field>
          <Field label="Revendeur">
            <div className="flex gap-1">
              <div className="flex-1"><SearchSelect testId="devis-revendeur-select" options={orgOptions(revendeurs)} value={d.revendeur_id || null} onChange={(v) => setField({ revendeur_id: v })} /></div>
              <button className="nx-btn-ghost !px-2" title="Créer un revendeur" onClick={() => setQuickOrg({ type: "revendeur", target: "revendeur_id" })}>+</button>
            </div>
          </Field>
          <Field label="Client final">
            <div className="flex gap-1">
              <div className="flex-1"><SearchSelect testId="devis-clientfinal-select" options={orgOptions(clients)} value={d.client_final_id || null} onChange={(v) => setField({ client_final_id: v })} /></div>
              <button className="nx-btn-ghost !px-2" title="Créer" onClick={() => setQuickOrg({ type: "client", target: "client_final_id" })}>+</button>
            </div>
          </Field>
          <Field label=" ">
            <label className="flex items-center gap-2 pt-2 text-sm">
              <input data-testid="devis-cfondoc-checkbox" type="checkbox" checked={!!d.cf_sur_document} onChange={(e) => setField({ cf_sur_document: e.target.checked })} />
              Afficher le client final sur le document
            </label>
          </Field>
          <Field label="Produit">
            <select data-testid="devis-produit-select" className="nx-input" value={d.produit_id || ""} onChange={(e) => setField({ produit_id: e.target.value || null, sous_produit_id: null })}>
              <option value="">—</option>
              {produits.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
            </select>
          </Field>
          <Field label="Sous-produit">
            <select data-testid="devis-sousproduit-select" className="nx-input" value={d.sous_produit_id || ""} onChange={(e) => setField({ sous_produit_id: e.target.value || null })} disabled={!sousProduits.length}>
              <option value="">—</option>
              {sousProduits.map((sp: any) => <option key={sp.id} value={sp.id}>{sp.nom}</option>)}
            </select>
          </Field>
          <Field label="Entité de facturation">
            <select data-testid="devis-entite-select" className="nx-input" value={d.entite_id || ""} onChange={(e) => setField({ entite_id: e.target.value || null })}>
              <option value="">—</option>
              {entites.map((e) => <option key={e.id} value={e.id}>{e.code} — {e.nom}</option>)}
            </select>
          </Field>
          <Field label="Devise">
            <select data-testid="devis-devise-select" className="nx-input" value={d.devise} onChange={(e) => setField({ devise: e.target.value })}>
              <option>EUR</option>
              <option>TND</option>
            </select>
          </Field>
          <Field label={d.statut === "reporte" ? "Nouvelle date de clôture estimée *" : "Date de clôture estimée"}>
            <input
              data-testid="devis-cloture-input"
              type="date"
              className={`nx-input ${d.statut === "reporte" && savedStatut !== "reporte" ? "border-amber ring-2 ring-amber/30" : ""}`}
              min={d.statut === "reporte" && savedStatut !== "reporte" ? todayISO() : undefined}
              required={d.statut === "reporte"}
              disabled={savedStatut === "reporte" && d.statut === "reporte"}
              title={savedStatut === "reporte" ? "Date reportée : en lecture seule jusqu'à réactivation" : undefined}
              value={(d.date_cloture_estimee || "").slice(0, 10)}
              onChange={(e) => setField({ date_cloture_estimee: e.target.value || null })}
            />
          </Field>
          {d.statut === "perdu" && (
            <>
              <Field label="Motif de perte *">
                <RefSelect
                  testId="devis-motif-select"
                  entries={listes.motif_perte || []}
                  value={d.motif_perte}
                  onChange={(v) => setField({ motif_perte: v })}
                  disabled={perduFige}
                  placeholder="Choisir un motif…"
                  className={!perduFige && !d.motif_perte ? "border-danger ring-2 ring-danger/20" : ""}
                />
              </Field>
              <Field label={d.motif_perte === motifAutre ? "Commentaire (obligatoire pour « Autre »)" : "Commentaire de perte"} className="col-span-2">
                <input
                  data-testid="devis-motif-commentaire-input"
                  className="nx-input"
                  disabled={perduFige}
                  value={d.motif_perte_commentaire || ""}
                  onChange={(e) => setField({ motif_perte_commentaire: e.target.value })}
                />
              </Field>
              {perduFige && (
                <Field label="Perdu le">
                  <p data-testid="devis-date-perte" className="pt-2 text-sm font-semibold text-danger">{fmtDate(d.date_perte)}</p>
                </Field>
              )}
            </>
          )}
          {mgr && (
            <Field label="Marge (saisie manuelle)">
              <input data-testid="devis-marge-input" type="number" className="nx-input" value={d.marge ?? ""} onChange={(e) => setField({ marge: e.target.value === "" ? null : +e.target.value })} />
            </Field>
          )}
          {mgr && d.marge_calculee_bc !== undefined && (
            <Field label="Marge calculée (BC liés)">
              <p className="pt-2 text-sm font-bold text-green" data-testid="devis-marge-bc">{fmtMoney(d.marge_calculee_bc, d.devise)}</p>
            </Field>
          )}
        </div>
      </div>

      <div className="nx-card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-bold text-green">Lignes</h2>
          <div className="flex gap-2">
            <button data-testid="ligne-add-btn" className="nx-btn-ghost !px-2.5 !py-1 text-xs" onClick={() => addLigne("ligne")}>+ Ligne</button>
            <button data-testid="titre-add-btn" className="nx-btn-ghost !px-2.5 !py-1 text-xs" onClick={() => addLigne("titre")}>+ Titre</button>
            <button data-testid="desc-add-btn" className="nx-btn-ghost !px-2.5 !py-1 text-xs" onClick={() => addLigne("desc")}>+ Description</button>
          </div>
        </div>
        <div className="space-y-1.5">
          {(d.lignes || []).length > 0 && (
            <div data-testid="lignes-header" className="flex items-center gap-2 px-[7px] text-xs font-semibold text-muted">
              <span className="w-[18px]" />
              <span className="flex-1">Désignation</span>
              <span className="w-20 text-right">Qté</span>
              <span className="w-20">Unité</span>
              <span className="w-28 text-right">P.U.</span>
              <span className="w-20 text-right">Remise %</span>
              <span className="w-28 text-right">Montant</span>
              <span className="w-[22px]" />
            </div>
          )}
          {(d.lignes || []).map((l: any, i: number) => (
            <div
              key={i}
              data-testid={`ligne-row-${i}`}
              draggable
              onDragStart={() => (dragIdx.current = i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(i)}
              className={`flex items-center gap-2 rounded-lg border p-1.5 ${l.type === "titre" ? "border-lime bg-lime-soft" : l.type === "desc" ? "border-line bg-soft/60" : "border-line bg-white"}`}
            >
              <span className="cursor-grab select-none px-1 text-muted" title="Glisser pour réordonner">⋮⋮</span>
              {l.type === "ligne" ? (
                <>
                  <input data-testid={`ligne-designation-${i}`} className="nx-input flex-1" placeholder="Désignation" value={l.designation || ""} onChange={(e) => updateLigne(i, { designation: e.target.value })} />
                  <input data-testid={`ligne-qte-${i}`} type="number" min="0" className="nx-input w-20 text-right" placeholder="Qté" value={l.qte ?? ""} onChange={(e) => updateLigne(i, { qte: clamp(+e.target.value, 0, 1e9) })} />
                  <input className="nx-input w-20" placeholder="Unité" value={l.unite || ""} onChange={(e) => updateLigne(i, { unite: e.target.value })} />
                  <input data-testid={`ligne-pu-${i}`} type="number" min="0" step="0.01" className="nx-input w-28 text-right" placeholder="P.U." value={l.prix_unitaire ?? ""} onChange={(e) => updateLigne(i, { prix_unitaire: clamp(+e.target.value, 0, 1e12) })} />
                  <input data-testid={`ligne-remise-${i}`} type="number" min="0" max="100" className="nx-input w-20 text-right" placeholder="Remise %" value={l.remise_pct ?? ""} onChange={(e) => updateLigne(i, { remise_pct: clamp(+e.target.value, 0, 100) })} />
                  <span className="w-28 text-right text-sm font-semibold">{fmtMoney((+l.qte || 0) * (+l.prix_unitaire || 0) * (1 - (+l.remise_pct || 0) / 100), d.devise)}</span>
                </>
              ) : (
                <input
                  data-testid={`ligne-designation-${i}`}
                  className={`nx-input flex-1 ${l.type === "titre" ? "font-bold text-green" : "italic text-muted"}`}
                  placeholder={l.type === "titre" ? "Titre de section (exclu des calculs)" : "Texte libre (exclu des calculs)"}
                  value={l.designation || ""}
                  onChange={(e) => updateLigne(i, { designation: e.target.value })}
                />
              )}
              <button data-testid={`ligne-delete-${i}`} className="px-1.5 text-danger hover:scale-110" onClick={() => removeLigne(i)} title="Supprimer la ligne">✕</button>
            </div>
          ))}
        </div>

        <div className="mt-5 grid gap-5 md:grid-cols-2">
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <Field label="Remise globale %">
                <input data-testid="devis-remiseglob-input" type="number" min="0" max="100" className="nx-input" value={d.remise_globale_pct ?? 0} onChange={(e) => setField({ remise_globale_pct: clamp(+e.target.value, 0, 100) })} />
              </Field>
              <Field label="Libellé remise">
                <input className="nx-input" value={d.remise_label || ""} onChange={(e) => setField({ remise_label: e.target.value })} />
              </Field>
              <Field label="TVA %">
                <select data-testid="devis-tva-select" className="nx-input" value={d.tva_pct ?? 0} onChange={(e) => setField({ tva_pct: +e.target.value })}>
                  {TVA_RATES.map((t) => <option key={t} value={t}>{t} %</option>)}
                </select>
              </Field>
              <Field label="Retenue à la source %">
                <select data-testid="devis-retenue-select" className="nx-input" value={d.retenue_pct ?? 0} onChange={(e) => setField({ retenue_pct: +e.target.value })}>
                  <option value={0}>0 %</option>
                  <option value={1}>1 %</option>
                </select>
              </Field>
            </div>
            <div>
              <label className="nx-label">Conditions de vente</label>
              <div className="space-y-1">
                {refs.conditions_vente.map((c: string) => (
                  <label key={c} className="flex items-center gap-2 text-sm">
                    <input
                      data-testid={`condition-checkbox-${refs.conditions_vente.indexOf(c)}`}
                      type="checkbox"
                      checked={(d.conditions || []).includes(c)}
                      onChange={(e) => setField({ conditions: e.target.checked ? [...(d.conditions || []), c] : (d.conditions || []).filter((x: string) => x !== c) })}
                    />
                    {c}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold">
                <input data-testid="devis-note-toggle" type="checkbox" checked={!!d.note_libre_on} onChange={(e) => setField({ note_libre_on: e.target.checked })} />
                Note libre sur le document
              </label>
              {d.note_libre_on && (
                <textarea data-testid="devis-note-input" className="nx-input mt-2" rows={2} value={d.note_libre || ""} onChange={(e) => setField({ note_libre: e.target.value })} />
              )}
            </div>
          </div>
          <div className="nx-card h-fit bg-soft/60 p-4" data-testid="devis-totaux">
            <h3 className="mb-2 text-sm font-bold text-green">Totaux</h3>
            <div className="space-y-1 text-sm">
              {totals.rg > 0 && (
                <>
                  <div className="flex justify-between"><span className="text-muted">Total HT</span><span data-testid="total-net">{fmtMoney(totals.net, d.devise)}</span></div>
                  <div className="flex justify-between"><span className="text-muted">{d.remise_label || "Remise"} ({d.remise_globale_pct} %)</span><span data-testid="total-remise">- {fmtMoney(totals.rg, d.devise)}</span></div>
                  <div className="flex justify-between font-semibold"><span>Net HT</span><span data-testid="total-ht">{fmtMoney(totals.ht, d.devise)}</span></div>
                </>
              )}
              {totals.rg <= 0 && <div className="flex justify-between font-semibold"><span>Total HT</span><span data-testid="total-ht">{fmtMoney(totals.ht, d.devise)}</span></div>}
              <div className="flex justify-between"><span className="text-muted">TVA ({d.tva_pct} %)</span><span data-testid="total-tva">{fmtMoney(totals.tva, d.devise)}</span></div>
              <div className="flex justify-between border-t border-line pt-1 text-base font-extrabold text-green"><span>Total TTC</span><span data-testid="total-ttc">{fmtMoney(totals.ttc, d.devise)}</span></div>
              {totals.retenue > 0 && (
                <>
                  <div className="flex justify-between"><span className="text-muted">Retenue à la source ({d.retenue_pct} %)</span><span>- {fmtMoney(totals.retenue, d.devise)}</span></div>
                  <div className="flex justify-between font-bold text-green"><span>Net à payer</span><span data-testid="total-napayer">{fmtMoney(totals.napayer, d.devise)}</span></div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {d.id && (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="nx-card p-4" data-testid="devis-documents-historique">
            <h3 className="mb-2 text-sm font-bold text-green">Documents générés</h3>
            {(d.documents || []).length === 0 ? <p className="text-sm text-muted">Aucun document généré</p> : (
              <ul className="max-h-48 space-y-1 overflow-y-auto">
                {(d.documents || []).map((doc: any) => (
                  <li key={doc.id} className="flex items-center justify-between rounded-lg bg-soft px-3 py-1.5 text-sm">
                    <span>
                      <Badge className="mr-2 bg-green text-white capitalize">{doc.type.replace("_", " ")}</Badge>
                      N° {doc.numero} {doc.entite_code && `· ${doc.entite_code}`} {doc.tampon_paye && "· PAYÉ"}
                    </span>
                    <span className="flex items-center gap-2 text-xs text-muted">
                      {fmtDateTime(doc.generated_at)}
                      <a
                        data-testid={`doc-download-${doc.id}`}
                        className="font-semibold text-green hover:underline"
                        href="#télécharger"
                        onClick={async (e) => {
                          e.preventDefault();
                          const res = await api.get(`/documents/${doc.id}/pdf`, { responseType: "blob" });
                          const url = URL.createObjectURL(res.data);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `${doc.type}_${doc.numero}.pdf`;
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                      >
                        Télécharger
                      </a>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="nx-card p-4" data-testid="devis-relances-historique">
            <h3 className="mb-2 text-sm font-bold text-green">Historique des relances</h3>
            {(d.relances || []).length === 0 ? <p className="text-sm text-muted">Aucune relance</p> : (
              <ul className="max-h-48 space-y-1 overflow-y-auto">
                {(d.relances || []).map((r: any) => (
                  <li key={r.id} className="rounded-lg bg-soft px-3 py-1.5 text-sm">
                    <span className="font-semibold">{r.date?.slice(0, 10).split("-").reverse().join("/")}</span>
                    {r.contact && <span className="text-muted"> — {r.contact}</span>}
                    {r.note && <p className="text-xs text-muted">{r.note}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {(d.historique_perte || []).length > 0 && (
            <div className="nx-card p-4 md:col-span-2" data-testid="devis-pertes-historique">
              <h3 className="mb-2 text-sm font-bold text-green">Historique perte / report</h3>
              <ul className="max-h-48 space-y-1 overflow-y-auto">
                {(d.historique_perte || []).map((h: any) => (
                  <li key={h.id} data-testid={`devis-perte-${h.id}`} className={`rounded-lg px-3 py-1.5 text-sm ${h.clos_le ? "bg-soft" : h.type === "perte" ? "bg-red-50" : "bg-amber-50"}`}>
                    <span className={`font-semibold ${h.type === "perte" ? "text-danger" : "text-amber"}`}>{h.type === "perte" ? "Perdu" : "Reporté"}</span>
                    <span className="text-muted"> le {fmtDateTime(h.created_at)}</span>
                    {h.type === "perte" ? (
                      <span> — motif : <span className="font-semibold">{h.motif || "non renseigné"}</span>{h.commentaire && <span className="text-muted"> ({h.commentaire})</span>}</span>
                    ) : (
                      <span> — nouvelle clôture estimée : <span className="font-semibold">{fmtDate(h.date_cloture_estimee)}</span></span>
                    )}
                    {h.statut_precedent && <span className="text-muted"> · depuis « {statutLabel(h.statut_precedent)} »</span>}
                    {h.auteur_nom && <span className="text-muted"> · {h.auteur_nom}</span>}
                    {h.clos_le && <span className="ml-1 text-xs text-muted">(réactivé le {fmtDateTime(h.clos_le)})</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {quickOrg && (
        <OrgQuickForm
          type={quickOrg.type}
          onClose={() => setQuickOrg(null)}
          onCreated={(org: any) => {
            setOrgs([...orgs, org]);
            setField({ [quickOrg.target]: org.id });
            setQuickOrg(null);
          }}
        />
      )}

      {pdfModal && (
        <Modal title={pdfModal === "facture" ? "Générer la facture" : "Générer la proforma"} onClose={() => setPdfModal(null)} testId="pdf-modal">
          <div className="space-y-3">
            <Field label="Entité émettrice">
              <select data-testid="pdf-entite-select" className="nx-input" value={pdfForm.entite_id || ""} onChange={(e) => setPdfForm({ ...pdfForm, entite_id: e.target.value })}>
                <option value="">— Choisir —</option>
                {entites.map((e) => <option key={e.id} value={e.id}>{e.code} — {e.nom}</option>)}
              </select>
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="pdf-paid-checkbox" type="checkbox" checked={!!pdfForm.paid} onChange={(e) => setPdfForm({ ...pdfForm, paid: e.target.checked })} />
              Apposer le tampon PAYÉ
            </label>
            <div className="flex justify-end gap-2">
              <button className="nx-btn-ghost" onClick={() => setPdfModal(null)}>Annuler</button>
              <button data-testid="pdf-generate-btn" className="nx-btn-primary" disabled={!pdfForm.entite_id || !!busy} onClick={() => genProforma(pdfModal === "facture")}>
                {busy ? "Génération…" : "Générer"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {sendModal && (
        <Modal title="Envoyer par email" onClose={() => setSendModal(null)} testId="send-modal">
          <div className="space-y-3">
            <Field label="Document à joindre">
              <select data-testid="send-document-select" className="nx-input" value={sendForm.document_id} onChange={(e) => setSendForm({ ...sendForm, document_id: e.target.value })}>
                <option value="">— Générer d'abord un PDF —</option>
                {(d.documents || []).map((doc: any) => (
                  <option key={doc.id} value={doc.id}>{doc.type} N° {doc.numero} {doc.entite_code ? `(${doc.entite_code})` : ""}</option>
                ))}
              </select>
            </Field>
            <Field label="Destinataires (séparés par des virgules)">
              <input data-testid="send-destinataires-input" className="nx-input" value={sendForm.destinataires} onChange={(e) => setSendForm({ ...sendForm, destinataires: e.target.value })} placeholder="client@exemple.com" />
            </Field>
            <Field label="Sujet">
              <input data-testid="send-sujet-input" className="nx-input" value={sendForm.sujet} onChange={(e) => setSendForm({ ...sendForm, sujet: e.target.value })} />
            </Field>
            <Field label="Corps">
              <textarea data-testid="send-corps-input" className="nx-input" rows={5} value={sendForm.corps} onChange={(e) => setSendForm({ ...sendForm, corps: e.target.value })} />
            </Field>
            {mailto && (
              <div data-testid="send-mailto-info" className="rounded-lg bg-amber-50 p-3 text-sm text-amber">
                <p className="mb-2">{mailto.info}</p>
                <a
                  className="font-bold underline"
                  href={`mailto:${mailto.to}?subject=${encodeURIComponent(mailto.subject)}&body=${encodeURIComponent(mailto.body)}`}
                >
                  Ouvrir dans votre messagerie
                </a>
              </div>
            )}
            {sendErr && <p data-testid="send-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-danger">{sendErr}</p>}
            <div className="flex justify-end gap-2">
              <button className="nx-btn-ghost" onClick={() => setSendModal(null)}>Fermer</button>
              <button data-testid="send-confirm-btn" className="nx-btn-primary" disabled={!sendForm.document_id || !sendForm.destinataires || !!busy} onClick={doSend}>
                {busy === "send" ? "Envoi…" : "Envoyer"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
